import api from '@/api/api';
import { message } from 'antd';
import { settingRequestParams, settingRequestWellParams } from './libs/formats/requestParams';
import { submitType } from '@/constants/reports';
import storeRematch from '@/store';
import { updateTreeData } from '@/components/Tree/lib';

const frameSettingParams = {
    'rc:LinkTarget': 'iFrame',
    'rc:Toolbar': 'False',
    'rs:Embed': 'False',
};

const submitSettingByType = {
    [submitType.CREATE]: 'HTML4.0',
    [submitType.DOWNLOAD]: 'EXCEL',
};

const findObjectById = (id, objects) => {
    for (const obj of objects) {
        if (obj.id === id) {
            return obj;
        }

        if (obj.children && Array.isArray(obj.children) && obj.children.length > 0) {
            const found = findObjectById(id, obj.children);
            if (found) {
                return found;
            }
        }
    }

    return null;
};

/*
    Параметр 'additionalBaseSettings' расширяет текущие БАЗОВЫЕ настройки iFrame (либо заменяет их)
*/
const getFrameSettingParams = settings => {
    const { submitType, additionalBaseSettings = {} } = settings;

    return {
        ...frameSettingParams,
        'rs:Format': submitSettingByType[submitType],
        'submitType': submitType, // поле нужно для отрисовки UI, не участвует в формировании отчета
        ...additionalBaseSettings,
    };
};

const reportsModel = {
    state: {
        matches: {
            isLoading: false,
            data: null,
            error: null,
        },
        formParams: null,
        isLoading: false,
    },
    reducers: {
        setMatches(state, payload) {
            return { ...state, matches: { ...state.matches, ...payload } };
        },
        setFormParams(state, payload) {
            return { ...state, formParams: payload };
        },
        setIsLoading(state, payload) {
            return { ...state, isLoading: payload };
        },
        resetFormParams(state) {
            return { ...state, formParams: null };
        },
    },
    effects: dispatch => ({
        // TODO: перенести запрос в соответствующую модель (tree report)
        async getTreeDataReports(options, rootState) {
            const { groupFilterValue } = rootState.tree;
            const { data, activeGroupKey, activeItemKey } = rootState.lists;
            const { buildTree } = dispatch.treeReports;
            const { setGdiWellsEffect } = dispatch.gdiFilterReport;

            const { signal, listItem, treeItem } = options;
            const foundedListItem =
                listItem ?? data.find(item => item.key === activeGroupKey || item.key === activeItemKey);
            const requestParams = resolveInitParams(foundedListItem, treeItem, groupFilterValue);

            try {
                this.setMatches({ isLoading: true, error: null });

                const result = await api.reports.get(requestParams, signal);

                if (result.name === 'Unknown' && listItem) result.name = listItem.title;

                this.setMatches({ isLoading: false, data: result });

                await buildTree();
                await setGdiWellsEffect();
            } catch (error) {
                /*
                    TODO: какую ошибку генерирует timeout error? Mb code 20.
                */
                if (error.name === 'AbortError') return;

                // eslint-disable-next-line no-console
                console.error('REPORT SIDEBAR ERROR', error);
                this.setMatches({ isLoading: false, error: new Error(error) });
            }
        },

        /*
            Шаг инициализации модуля (возможно, будет расширяться)
            - запрос за деревом
            - включить переключатель работы с деревом, если выбрана скважина (или группа скважин)
        */
        init(options, rootState) {
            const { activeItemKey, activeGroupKey } = rootState.lists;

            const isInteraction = Boolean(activeItemKey || activeGroupKey);

            dispatch.baseFilterReport.setTreeInteraction(isInteraction);
            this.getTreeDataReports(options);
        },

        /*
            Общая сборка параметров
        */
        async finishWithBaseParams(settings) {
            const { getFilterParams } = dispatch.filterReport;

            // Конфигурация фильтра
            const filterParams = await getFilterParams();

            // Конфигурация базовых настроек
            const frameParams = getFrameSettingParams(settings);

            return merge(filterParams, frameParams);
        },

        /*
            Шаг сбора информации для получения отчета c учетом работы с WSE деревом
        */
        async finishWithTree(submit = submitType.CREATE) {
            const { getInfoParams } = dispatch.infoReports;
            const { getTreeParams } = dispatch.treeReports;
            const { setCalculationObject, setData } = dispatch.tree;

            const state = storeRematch.getState();
            const {
                filterReport: { reportType },
                tree,
                lists,
            } = state;
            const isMer = reportType[0] === 'Mer';

            const { calculationObject, groupFilterValue } = tree;
            const { activeGroupKey, activeItemKey, data } = lists;

            /*
                Формируется путь до нужного отчета из выбранного reportType в фильтре.
            */

            /*
                ? TODO: mb Promise.all
            */
            try {
                if (isMer && calculationObject?.nodeType === 'organization') {
                    const responseVenture = await api.tree.fetchData({
                        params: calculationObject.urlParam,
                        groupView: groupFilterValue,
                    });

                    const ventureData = updateTreeData(tree.data, calculationObject.key, responseVenture);
                    const currentIndex = ventureData.findIndex(item => item.key === calculationObject.key);
                    const newCalculationObject = ventureData[currentIndex];
                    const promisesArray = [];

                    newCalculationObject.children.forEach(item => {
                        promisesArray.push(
                            api.tree.fetchData({
                                params: item.urlParam,
                                groupView: groupFilterValue,
                            }),
                        );
                    });

                    const res = await Promise.all(promisesArray);

                    res.forEach((fieldResponse, i) => {
                        newCalculationObject.children[i].children = fieldResponse;
                    });

                    ventureData[currentIndex] = newCalculationObject;

                    setData(ventureData);
                    setCalculationObject(newCalculationObject);
                }
                let fieldIds = '';
                let ventureIds = '';
                {
                    if (activeGroupKey || activeItemKey) {
                        const fieldIdsRaw = new Set();
                        const ventureIdsRaw = new Set();
                        const addFieldAndVentureIds = item => {
                            const parentField = item.parents.find(({ node }) => node === 'field')?.id;
                            const parentVenture = item.parents.find(({ node }) => node === 'company')?.id;
                            fieldIdsRaw.add(parentField);
                            ventureIdsRaw.add(parentVenture);
                        };

                        const activeItem = data.find(item => item.key === activeGroupKey || item.key === activeItemKey);

                        if (activeItem?.children?.length) {
                            // в случае выбранной группы
                            activeItem.children.forEach(child => {
                                if (!child.parents) {
                                    const item = findObjectById(child.id, tree.data);

                                    child.parents = item.parents;
                                }

                                addFieldAndVentureIds(child);
                            });
                        } else {
                            // в случае выбранной скважины
                            if (!activeItem.parents) {
                                const item = findObjectById(activeItem.id, tree.data);

                                activeItem.parents = item.parents;
                            }
                            addFieldAndVentureIds(activeItem);
                        }

                        fieldIds = [...fieldIdsRaw].join(',');
                        ventureIds = [...ventureIdsRaw].join(',');
                    }
                }
                const params = await dispatch.reports.finishWithBaseParams({ submitType: submit });
                if (calculationObject?.node === 'field') {
                    ventureIds = calculationObject.parents.find(({ node }) => node === 'company')?.id;
                }
                // Конфигурация информации из информационного дерева
                const infoParams = await getInfoParams();

                // Конфигурация дерева WSE
                const treeParams = await getTreeParams();

                let resultParams = merge(params, infoParams, treeParams);
                const ventureId = ventureIds
                    ? ventureIds
                    : calculationObject?.node === 'company'
                      ? calculationObject.id
                      : calculationObject?.node === 'organization'
                        ? 0
                        : calculationObject?.parentVentureId
                          ? calculationObject?.parentVentureId
                          : 0;
                const orgId =
                    calculationObject?.node === 'organization'
                        ? calculationObject.id
                        : calculationObject?.node === 'company'
                          ? calculationObject?.parentVentureId
                          : 0;
                resultParams.Selected_Field_IDs = fieldIds
                    ? fieldIds
                    : calculationObject?.node === 'organization' || calculationObject?.node === 'company'
                      ? 0
                      : resultParams.Selected_Field_IDs
                        ? resultParams.Selected_Field_IDs
                        : calculationObject?.id
                          ? calculationObject?.id
                          : 0;

                resultParams = { ...resultParams, Selected_Venture_IDs: ventureId, Selected_Organization_IDs: orgId };

                if (isMer) {
                    const whiteListParams = [
                        'Selected_Workshop_IDs',
                        'Selected_Field_IDs',
                        'Selected_Area_IDs',
                        'Selected_Mine_IDs',
                        'Selected_Well_IDs',
                        'Selected_Stratum_IDs',
                        'Selected_Venture_ID',
                        'Selected_Element_IDs',
                        'Selected_Criterion_IDs',
                        'Selected_Triplet_IDs',
                        'YearBegin',
                        'MonthBegin',
                        'YearEnd',
                        'MonthEnd',
                        'Year',
                        'Month',
                        'reportType',
                        'submitType',
                        // Grouping params from Form
                        'useGrouping',
                    ];

                    const resultParamsArray = Object.entries(resultParams).filter(([key, _]) =>
                        whiteListParams.includes(key),
                    );

                    resultParams = Object.fromEntries(resultParamsArray);

                    resultParams = {
                        ...resultParams,
                        'rc:LinkTarget': 'iFrame',
                        'rc:Toolbar': 'False',
                        'rs:Embed': 'False',
                        'rs:Format': submit === submitType.CREATE ? 'HTML4.0' : 'EXCEL',
                    };
                }

                this.setFormParams(resultParams);
            } catch (error) {
                /*
                    Если какая либо функция конфигурации упадет, уведомляем пользователя и НЕ формирует отчет.
                */
                message.error('Не удалось сформировать отчет.');
                // eslint-disable-next-line no-console
                console.log('reports::finish call error', error);
            }
        },

        /*
            Шаг сбора информации для получения отчета БЕЗ учета работы с WSE деревом
        */
        async finishWithoutTree(submit = submitType.CREATE) {
            try {
                const params = await dispatch.reports.finishWithBaseParams({
                    submitType: submit,
                });

                this.setFormParams(params);
            } catch (error) {
                /*
                    Если какая либо функция конфигурации упадет, уведомляем пользователя и НЕ формирует отчет.
                */
                message.error('Не удалось сформировать отчет.');
                // eslint-disable-next-line no-console
                console.log('reports::finish call error', error);
            }
        },
    }),
};

export const resolveInitParams = (listItem, treeItem, groupValue) => {
    if (listItem) return settingRequestWellParams(listItem);
    if (treeItem) return settingRequestParams(groupValue, treeItem);

    return () => {};
};

function merge(...args) {
    return args.reduce((acc, item) => {
        return Object.assign(acc, item);
    }, {});
}

export default reportsModel;
